import { useMemo } from "react";
import { GroupedVirtualized, type IGroupedVirtualizedProps } from "../GroupedVirtualized";
import { VirtuosoSelectGroup } from "./Group";
import { virtuosoHeader } from "./Header";
import { VirtuosoSelectItem } from "./Item";
import { VirtuosoTopItemList } from "./TopItemList";
import { GroupedVirtualizedSelectProvider, type IGroupedVirtualizedSelectContext } from "./context";
import { useMixSlotProps } from "./logics";

export interface IGroupedVirtualizedSelectProps<Data, Context> extends IGroupedVirtualizedProps<Data, Context> {
  selectedItems?: readonly Data[];
  onSelectedItemsChange?: (items: readonly Data[]) => void;
  getItemKey: (item: Data) => string;
  hideCheckboxes?: boolean;
  slotProps?: IGroupedVirtualizedProps<Data, Context>["slotProps"] &
    IGroupedVirtualizedSelectContext<Data>["slotProps"];
}

export const GroupedVirtualizedSelect = <Data, Context>({
  data,
  groupCounts,
  selectedItems,
  onSelectedItemsChange,
  getItemKey,
  hideCheckboxes = false,
  slotProps,
  ...props
}: IGroupedVirtualizedSelectProps<Data, Context>) => {
  const [selectSlotProps, groupedSlotProps] = useMixSlotProps(slotProps);

  const { Header, ...components } = props.components ?? {};
  const SelectHeader = useMemo(() => Header && virtuosoHeader(Header), [Header]);

  return (
    <GroupedVirtualizedSelectProvider
      data={data}
      groupCounts={groupCounts}
      selectedItems={selectedItems}
      onSelectedItemsChange={onSelectedItemsChange}
      hideCheckboxes={hideCheckboxes}
      slotProps={selectSlotProps}
      getItemKey={getItemKey}
    >
      <GroupedVirtualized
        {...props}
        data={data}
        groupCounts={groupCounts}
        slotProps={groupedSlotProps}
        components={{
          ...components,
          Group: VirtuosoSelectGroup,
          Item: VirtuosoSelectItem,
          TopItemList: VirtuosoTopItemList,
          Header: SelectHeader,
        }}
      />
    </GroupedVirtualizedSelectProvider>
  );
};
